import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Check,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCopy,
  ExternalLinkIcon,
} from 'lucide-react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import CarrierVerificationCard from 'components/CarrierVerification/CarrierVerificationCard';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography/Typography';
import { useFetchEquipmentTypes } from 'hooks/useFetchEquipmentTypes';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { newTMSHost } from 'lib/hosts/tms/interface';
import {
  Dropoff,
  Load,
  LoadStopsItem,
  LoadStopsItemTypes,
  Pickup,
  isDeliveryStopType,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import captureException from 'utils/captureException';
import { copyToClipboard } from 'utils/copyToClipboard';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';

import { buildLoadSummary } from './utils/buildLoadSummary';

interface OverviewSectionProps {
  tmsName: TMS;
  load: Load;
  fromEmail: Maybe<string>;
  loadCloneControl?: ReactNode;
  loadCloneRecap?: ReactNode;
}

const popoverRootContainer = getDrumkitContainer();

interface CopyableBadgeProps {
  text: string;
  badgeId: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
  copiedBadgeId: Maybe<string>;
  onCopy: (text: string, badgeId: string) => Promise<void>;
  role?: string;
  portalContainer?: Maybe<Element>;
}

function CopyableBadge({
  text,
  badgeId,
  variant = 'outline',
  className = '',
  copiedBadgeId,
  onCopy,
  role,
  portalContainer,
}: CopyableBadgeProps) {
  const isCopied = copiedBadgeId === badgeId;
  const baseClassName =
    'cursor-pointer select-none transition-all hover:scale-105 active:scale-95';
  const hoverClassName = className.includes('bg-brand')
    ? 'hover:bg-brand-100'
    : 'hover:bg-neutral-100';

  const tooltipContent = (
    <TooltipContent className='text-xs'>
      {isCopied ? (
        <Flex align='center' gap='xs'>
          <Check size={14} className='stroke-success' />
          <span>Copied</span>
        </Flex>
      ) : (
        'Click to copy'
      )}
    </TooltipContent>
  );

  return (
    <Tooltip
      delayDuration={isCopied ? 0 : 500}
      open={isCopied ? true : undefined}
    >
      <TooltipTrigger asChild>
        <Badge
          variant={variant}
          className={`${baseClassName} ${hoverClassName} ${className}`}
          onClick={() => onCopy(text, badgeId)}
          role={role}
        >
          {text}
        </Badge>
      </TooltipTrigger>
      {portalContainer ? (
        <TooltipPortal container={portalContainer}>
          {tooltipContent}
        </TooltipPortal>
      ) : (
        tooltipContent
      )}
    </Tooltip>
  );
}

export default function OverviewSection({
  tmsName,
  load,
  fromEmail,
  loadCloneControl,
  loadCloneRecap,
}: OverviewSectionProps) {
  const {
    externalTMSID,
    freightTrackingID,
    pickup,
    consignee,
    status: loadStatus,
    moreThanTwoStops,
    stops,
    mode,
  } = load;

  // MGB stores human-friendly Mode on rateData.customerRateType (e.g. Truckload);
  // load.mode is often TL. Exclude "Flat" (rate type, not Mode — ENG-7739).
  const modeDisplay = (() => {
    if (tmsName !== TMS.MercurygateBoomi) {
      return mode;
    }
    const fromRate = load.rateData?.customerRateType?.trim() || '';
    if (fromRate && fromRate.toLowerCase() !== 'flat') {
      return fromRate;
    }
    const fromMode = mode?.trim() || '';
    if (fromMode && fromMode.toLowerCase() !== 'flat') {
      return fromMode;
    }
    // Flat or empty → same default as Rates form.
    return fromRate || fromMode ? 'Truckload' : '';
  })();

  const [showAllRouteStops, setShowAllRouteStops] = useState(false);
  const [copiedBadgeId, setCopiedBadgeId] = useState<Maybe<string>>(null);
  const [isLoadSummaryPreviewOpen, setIsLoadSummaryPreviewOpen] =
    useState(false);
  const copyTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);

  const {
    serviceFeaturesEnabled: {
      isMultiStopLoadViewEnabled,
      isCarrierVerificationEnabled,
      isLoadSummaryCopyEnabled,
    },
    carrierVerificationIntegrations,
  } = useServiceFeatures();

  // Revenova: BE stores the Salesforce equipment record ID; show its catalog name.
  const { equipmentTypes: revenovaEquipmentTypes } = useFetchEquipmentTypes(
    tmsName === TMS.Revenova ? load.tmsID : 0
  );
  const catalogEquipmentName = revenovaEquipmentTypes?.find(
    (et) => et.id === load.specifications?.transportType
  )?.name;

  const loadSummary = useMemo(
    () =>
      isLoadSummaryCopyEnabled
        ? buildLoadSummary(load, tmsName, catalogEquipmentName)
        : '',
    [isLoadSummaryCopyEnabled, load, tmsName, catalogEquipmentName]
  );

  let loadRoute = [
    {
      label: getRouteLabelPickupDropoff(pickup),
      type: LoadStopsItemTypes.Pickup,
    },
    {
      label: getRouteLabelPickupDropoff(consignee),
      type: LoadStopsItemTypes.Delivery,
    },
  ];

  if (moreThanTwoStops && stops?.length) {
    loadRoute = stops.map((s) => ({
      label: getRouteLabelStop(s),
      type: s.stopType,
    }));
  }

  const poNumbers = load.poNums ? load.poNums.split(',') : [];
  const validPONumbers = poNumbers.filter(
    (item) => item && item.trim().length > 0
  );
  const { length } = validPONumbers;
  const displayedPONumbers = validPONumbers.slice(0, 2);
  const remainingPOCount = length - displayedPONumbers.length;

  // MGB: BE stores the readable equipment label on transportType and clears
  // transportSize. Fall back to size only for legacy rows that still keep the
  // name there with an opaque code on type.
  const equipmentType =
    tmsName === TMS.MercurygateBoomi
      ? load.specifications?.transportType?.trim() ||
        load.specifications?.transportSize?.trim() ||
        ''
      : catalogEquipmentName || load.specifications?.transportType;
  const carrierName = load.carrier?.name;
  // MGB: LD # lives on customer.refNumber (loads.customer_ref_number); show as BOL.
  const bolNumber =
    tmsName === TMS.MercurygateBoomi
      ? load.customer?.refNumber?.trim() || ''
      : '';
  // MGB hides legacy auto-generated Order Number (poNums); other TMS keep PO #.
  const showPoNumbers =
    tmsName !== TMS.MercurygateBoomi && validPONumbers.length > 0;

  const pickupStopCount = moreThanTwoStops
    ? stops?.filter((s) => s.stopType === LoadStopsItemTypes.Pickup).length
    : 1;

  const dropoffStopCount = moreThanTwoStops
    ? stops?.filter((s) => isDeliveryStopType(s.stopType)).length
    : 1;

  let externalTMSIDMoniker, freightTrackingIDMoniker;

  // Get the first enabled carrier verification integration
  const enabledCarrierVerificationIntegration =
    carrierVerificationIntegrations?.[0] ?? null;

  // TODO: add more tms integration url/support for hyperlink to integration load page
  const integrationLoadPageUrls: Record<TMS | string, string> = {
    [TMS.Turvo]: `https://app.turvo.com/#/SHc59HSy/shipments/${externalTMSID}`,
  };

  try {
    const tmsClient = newTMSHost(tmsName);
    externalTMSIDMoniker = tmsClient?.getFieldMoniker('externalTMSID');
    freightTrackingIDMoniker = tmsClient?.getFieldMoniker('freightTrackingID');
  } catch (e) {
    if (!isProd())
      captureException(e, { tmsName, functionName: 'OverviewSection' });
  }

  const externalLinkLabel = useMemo(() => {
    switch (tmsName) {
      case TMS.Turvo:
        return `${titleCase(tmsName)} Shipment`;
      default:
        return `${titleCase(tmsName)} Load`;
    }
  }, [tmsName]);

  const handleBadgeClick = async (text: string, badgeId: string) => {
    if (!text) {
      return;
    }
    const success = await copyToClipboard(text.trim(), {
      showToast: false,
    });

    if (success) {
      setCopiedBadgeId(badgeId);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setCopiedBadgeId(null);
      }, 2000);
    }
  };

  const handleCopyLoadSummary = async () => {
    if (!loadSummary.trim()) {
      return;
    }
    await copyToClipboard(loadSummary, {
      toastMessage: 'Load summary copied',
    });
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TooltipProvider>
      <Flex direction='col' gap='sm' className='pb-4 w-full text-sm'>
        {externalTMSID && externalTMSIDMoniker && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='h6' className='text-neutral-800'>
              {externalTMSIDMoniker}
            </Typography>
            <CopyableBadge
              text={externalTMSID}
              badgeId='externalTMSID'
              variant='outline'
              className='border-brand bg-brand-50 text-brand capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </Flex>
        )}

        {/* MGB: surface LD # (BOL) first; */}
        {bolNumber && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='h6' className='text-neutral-800'>
              BOL
            </Typography>
            <CopyableBadge
              text={bolNumber}
              badgeId='bolNumber'
              variant='outline'
              className='border-brand bg-brand-50 text-brand capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </Flex>
        )}

        {freightTrackingID && freightTrackingIDMoniker && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='h6' className='text-neutral-800'>
              {freightTrackingIDMoniker}
            </Typography>
            <CopyableBadge
              text={freightTrackingID}
              badgeId='freightTrackingID'
              variant='outline'
              className='border-neutral-500 capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </Flex>
        )}

        {loadStatus && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='h6' className='text-neutral-800'>
              Load Status
            </Typography>
            <CopyableBadge
              text={loadStatus}
              badgeId='loadStatus'
              variant='outline'
              className='border-brand bg-brand-50 text-brand capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </Flex>
        )}

        {isMultiStopLoadViewEnabled && moreThanTwoStops && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='h6' className='text-neutral-800'>
              Stops
            </Typography>
            <Flex align='center' gap='sm'>
              <Tooltip delayDuration={10}>
                <TooltipTrigger asChild>
                  <Badge
                    variant='outline'
                    className='border-neutral-400 flex items-center gap-1'
                  >
                    <span>{pickupStopCount}</span>
                    <ArrowUpIcon size={14} className='!stroke-brand' />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className='text-xs'>
                  Number of pickup locations
                </TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={10}>
                <TooltipTrigger asChild>
                  <Badge
                    variant='outline'
                    className='border-neutral-400 flex items-center gap-1'
                  >
                    <span>{dropoffStopCount}</span>
                    <ArrowDownIcon size={14} className='!stroke-success' />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className='text-xs'>
                  Number of dropoff locations
                </TooltipContent>
              </Tooltip>
            </Flex>
          </Flex>
        )}

        {/* Origin/Destination */}
        {loadRoute.length > 0 && (
          <Flex gap='xl' justify='between' className='w-full px-4'>
            <h2 className='text-neutral-800 text-sm'>O/D</h2>
            <Flex direction='col' align='end' gap='xs'>
              {/* First stop */}
              {getRouteStopBadge(
                loadRoute[0].label,
                loadRoute[0].type === LoadStopsItemTypes.Pickup,
                handleBadgeClick,
                copiedBadgeId,
                0
              )}

              {/* Intermediate stops */}

              {loadRoute.length > 2 && (
                <>
                  {!showAllRouteStops ? (
                    <Button
                      buttonNamePosthog={ButtonNamePosthog.ShowAllRouteStops}
                      variant='ghost'
                      className='h-7 gap-0.5 mr-5.5 items-center text-xs text-neutral-800 underline px-0 border-none hover:border-none'
                      size='sm'
                      onClick={() => setShowAllRouteStops(!showAllRouteStops)}
                    >
                      <ChevronDownIcon size={16} />
                      {`${loadRoute.length - 2} interim stops`}
                    </Button>
                  ) : (
                    <>
                      <Button
                        buttonNamePosthog={ButtonNamePosthog.ShowAllRouteStops}
                        variant='ghost'
                        className='h-7 gap-0.5 mr-5.5 items-center text-xs text-neutral-800 underline px-0 border-none hover:border-none'
                        size='sm'
                        onClick={() => setShowAllRouteStops(!showAllRouteStops)}
                      >
                        <ChevronUpIcon size={16} />
                        {`Hide interim stops`}
                      </Button>

                      {loadRoute.map((route, i) => {
                        if (i === 0 || i === loadRoute.length - 1) {
                          return null;
                        }

                        return getRouteStopBadge(
                          route.label,
                          route.type === LoadStopsItemTypes.Pickup,
                          handleBadgeClick,
                          copiedBadgeId,
                          i
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {/* Last stop */}
              {getRouteStopBadge(
                loadRoute[loadRoute.length - 1].label,
                loadRoute[loadRoute.length - 1].type ===
                  LoadStopsItemTypes.Pickup,
                handleBadgeClick,
                copiedBadgeId,
                loadRoute.length - 1
              )}
            </Flex>
          </Flex>
        )}

        {/* Mode */}
        {modeDisplay && (
          <div className='flex flex-row flex-wrap gap-2 justify-between w-full px-4 items-center'>
            <h2 className='text-neutral-800 text-sm'>Mode</h2>
            <CopyableBadge
              text={modeDisplay}
              badgeId='mode'
              variant='outline'
              className='border-neutral-500 capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </div>
        )}

        {/* Equipment Type */}
        {equipmentType && (
          <div className='flex flex-row flex-wrap gap-2 justify-between w-full px-4 items-center'>
            <h2 className='text-neutral-800 text-sm'>Equipment Type</h2>
            <CopyableBadge
              text={equipmentType}
              badgeId='equipmentType'
              variant='outline'
              className='border-neutral-500 capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </div>
        )}

        {/* PO # — hidden for MGB (legacy Order Number); BOL shown above for MGB. */}
        {showPoNumbers && (
          <Flex
            justify='between'
            gap='2xl'
            className={`w-full px-4
              ${validPONumbers.length > 2 ? 'items-start' : 'items-center'}`}
          >
            <Typography variant='h6' className='text-neutral-800 shrink-0'>
              PO #
            </Typography>
            <Flex justify='end' gap='sm' wrap='wrap' role='list'>
              {displayedPONumbers.map(
                (item, idx) =>
                  item &&
                  item.trim().length > 0 && (
                    <CopyableBadge
                      key={idx}
                      text={item.trim()}
                      badgeId={`po-${idx}`}
                      variant='outline'
                      className='border-neutral-500 capitalize'
                      copiedBadgeId={copiedBadgeId}
                      onCopy={handleBadgeClick}
                      role='listitem'
                    />
                  )
              )}
              {remainingPOCount > 0 && (
                <Typography variant='caption' className='text-neutral-800'>
                  +{remainingPOCount} more
                </Typography>
              )}
            </Flex>
          </Flex>
        )}

        {/* Carrier Name */}
        {carrierName && (
          <div className='flex flex-row flex-wrap gap-2 justify-between w-full px-4 items-center'>
            <h2 className='text-neutral-800 text-sm'>Carrier</h2>
            <CopyableBadge
              text={carrierName}
              badgeId='carrierName'
              variant='outline'
              className='border-neutral-500 capitalize'
              copiedBadgeId={copiedBadgeId}
              onCopy={handleBadgeClick}
            />
          </div>
        )}

        {/* Hyper link to integration load page */}
        {tmsName in integrationLoadPageUrls && (
          <Flex
            justify='between'
            align='center'
            gap='sm'
            wrap='wrap'
            className='w-full px-4'
          >
            <Typography variant='body-sm'>{externalLinkLabel}</Typography>
            <a
              href={integrationLoadPageUrls[tmsName]}
              target='_blank'
              rel='noopener noreferrer'
              className='text-brand-main text-sm underline gap-1 flex items-center'
            >
              <Typography variant='body-sm'>View in TMS</Typography>
              <ExternalLinkIcon size={16} />
            </a>
          </Flex>
        )}

        {loadCloneRecap}
        {loadCloneControl && (
          <div className='w-full px-4 pt-1'>{loadCloneControl}</div>
        )}

        {isLoadSummaryCopyEnabled && (
          <Flex direction='col' gap='sm' className='w-full px-4 pt-1'>
            <Collapsible
              open={isLoadSummaryPreviewOpen}
              onOpenChange={setIsLoadSummaryPreviewOpen}
            >
              <Flex gap='xs' align='stretch' className='w-full'>
                <Button
                  buttonNamePosthog={ButtonNamePosthog.CopyLoadSummary}
                  variant='secondary'
                  size='sm'
                  className='min-w-0 flex-1 gap-2 text-sm'
                  onClick={handleCopyLoadSummary}
                  disabled={!loadSummary.trim()}
                  data-testid='copy-load-summary-button'
                >
                  <ClipboardCopy size={16} />
                  <Typography variant='body-sm'>Copy load summary</Typography>
                </Button>
                <CollapsibleTrigger asChild>
                  <Button
                    buttonNamePosthog={
                      ButtonNamePosthog.ToggleLoadSummaryPreview
                    }
                    variant='secondary'
                    size='sm'
                    className='shrink-0 px-2'
                    aria-label={
                      isLoadSummaryPreviewOpen
                        ? 'Hide load summary preview'
                        : 'Show load summary preview'
                    }
                    aria-expanded={isLoadSummaryPreviewOpen}
                    data-testid='toggle-load-summary-preview-button'
                  >
                    {isLoadSummaryPreviewOpen ? (
                      <ChevronUpIcon size={16} />
                    ) : (
                      <ChevronDownIcon size={16} />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </Flex>
              <CollapsibleContent>
                <Flex
                  direction='col'
                  className='mt-2 rounded-[4px] border border-neutral-200 bg-neutral-50 px-3 py-2'
                  data-testid='load-summary-preview'
                >
                  {loadSummary.trim() ? (
                    <Typography
                      variant='body-sm'
                      className='whitespace-pre-wrap text-neutral-800'
                    >
                      {loadSummary}
                    </Typography>
                  ) : (
                    <Typography variant='body-sm' className='text-neutral-500'>
                      No load details available to summarize.
                    </Typography>
                  )}
                </Flex>
              </CollapsibleContent>
            </Collapsible>
          </Flex>
        )}

        {isCarrierVerificationEnabled && fromEmail && (
          <CarrierVerificationCard
            fromEmail={fromEmail}
            enabledIntegration={enabledCarrierVerificationIntegration}
          />
        )}
      </Flex>
    </TooltipProvider>
  );
}

function getRouteLabelStop(stop: LoadStopsItem) {
  return stop?.address?.city && stop?.address?.state
    ? `${_.startCase(_.toLower(stop.address.city))}, ${stop.address.state}`
    : stop?.address?.city || stop?.address?.name;
}

function getRouteLabelPickupDropoff(loadStop: Pickup | Dropoff) {
  return loadStop?.city && loadStop?.state
    ? `${_.startCase(_.toLower(loadStop.city))}, ${loadStop.state}`
    : loadStop?.city || loadStop?.name;
}

function getRouteStopBadge(
  label: Undef<string>,
  isPickup: boolean,
  onBadgeClick?: (text: string, badgeId: string) => Promise<void>,
  copiedBadgeId?: Maybe<string>,
  index?: number
) {
  if (!label) {
    return null;
  }
  const badgeId = `route-stop-${index ?? 'unknown'}-${label}`;
  return (
    <Flex align='center' gap='xs'>
      {onBadgeClick ? (
        <CopyableBadge
          text={label}
          badgeId={badgeId}
          variant='outline'
          className='border-neutral-500 capitalize px-1.5 text-[11px]'
          copiedBadgeId={copiedBadgeId || null}
          onCopy={onBadgeClick}
          portalContainer={popoverRootContainer as Maybe<HTMLElement>}
        />
      ) : (
        <Badge
          variant='outline'
          className='border-neutral-500 capitalize px-1.5 text-[11px]'
        >
          {label}
        </Badge>
      )}
      <Tooltip delayDuration={10}>
        <TooltipTrigger asChild>
          {isPickup ? (
            <ArrowUpIcon size={16} className='text-neutral-800 !stroke-brand' />
          ) : (
            <ArrowDownIcon
              size={16}
              className='text-neutral-800 !stroke-success'
            />
          )}
        </TooltipTrigger>
        <TooltipPortal container={popoverRootContainer}>
          <TooltipContent className='text-xs'>
            {isPickup ? 'Pickup' : 'Delivery'}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </Flex>
  );
}
